import { PlayArrow } from '@mui/icons-material'
import { Box, Button, Skeleton, Typography } from '@mui/material'
import useCurrentMenu from 'common/hooks/useCurrentMenu'
import { ModalTypes } from 'common/hooks/useCustomModal'
import i18n from 'lang/idiomas'
import { enqueueSnackbar } from 'notistack'
import { useEffect, useState } from 'react'
import useFetchTipoSalida from '../hooks/useFetchTipoSalida'
import FichajesController, { PresenceControlActions } from '../libs/FichajesController'
import { colorSalidaMap, tiposSalidaMap } from '../libs/tiposSalidaMap'

/**
 * @module ControlPresencia
 */

/**
 * Buttons to mark enter or leave of
 * the presence control
 *
 * @param {Object} props
 * @param {boolean} props.loading
 * @param {boolean} props.modal
 * @param {function} props.setLastIteration
 * @param {number} props.lastIteration
 * @param {import('common/contexts/CustomModalContext').CustomModalHandler} props.handleModal
 * @example
 * ...
 * <PresenceActions
 *  loading={loading}
 *  setLastIteration={setLastIteration}
 *  lastIteration={lastIteration}
 *  handleModal={handleModal}
 *  modal={modal}
 * />
 * ...
 * @return {JSX.Element}
 */
const PresenceActions = ({
  loading,
  modal,
  setLastIteration,
  lastIteration,
  handleModal,
  card,
  inColumns = false,
  fontSize = 'inherit',
  onError = () => {},
  onSuccess = () => {},
}) => {
  const menu = useCurrentMenu()
  const { response: tiposEntradaSalida, loading: loadingTiposSalida } = useFetchTipoSalida()

  const [cardCode, setCardCode] = useState('')

  useEffect(() => {
    if (card) {
      setCardCode(card)
    }
  }, [card])

  /**
   * Mark a presence control registry
   * @async
   * @param {PresenceControlActions} type - The tipe of action to perform
   * @param {string} [exitCode] - If is a exit type this param must be provided
   * @returns {Promise<void>}
   * */
  async function markAction(type, exitCode) {
    try {
      handleModal({
        type: ModalTypes.Loading,
        enabled: true,
        content: (
          <Typography sx={{ color: 'text.primary' }}>
            {i18n.t('clkinloa', { defaultValue: 'Registrando fichaje' })}
          </Typography>
        ),
      })
      const res = await FichajesController.create(
        type,
        exitCode,
        cardCode,
        menu?.actualMatch?.parametros?.TERMINAL,
      )
      onSuccess(res)
      if (setLastIteration) setLastIteration(lastIteration + 1)
    } catch (error) {
      enqueueSnackbar('Ha ocurrido un error', {
        variant: 'error',
      })
      onError()
    } finally {
      handleModal({ type: ModalTypes.Loading, enabled: false })
    }
  }

  return loading || loadingTiposSalida ? (
    <Box
      sx={{
        display: 'flex',
        flexDirection: inColumns ? 'column' : 'row',
        alignItems: 'flex-start',
        gap: 2,
        width: '100%',
      }}
    >
      <Skeleton variant="rounded" height={40} width={240} />
      <Skeleton variant="rounded" height={40} width={240} />
      <Skeleton variant="rounded" height={40} width={240} />
    </Box>
  ) : (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'flex-start',
        gap: 2,
        width: '100%',
      }}
    >
      <Button
        startIcon={<PlayArrow sx={{ fontSize: fontSize }} />}
        disabled={loading || modal}
        onClick={async () => {
          markAction(PresenceControlActions.Entrada)
        }}
        variant="contained"
        color="success"
        sx={{ flexGrow: 0, textWrap: 'nowrap', fontSize: fontSize }}
      >
        {i18n.t('clockin', { defaultValue: 'Entrada' })}
      </Button>
      <Box
        sx={{
          flex: '1 1 0',
          display: 'flex',
          flexDirection: inColumns ? 'column' : 'row',
          gap: 2,
          flexWrap: 'wrap',
        }}
      >
        {tiposEntradaSalida?.tipos_salida?.map((exit, index) => {
          const Icon = tiposSalidaMap[exit.codigo] || tiposSalidaMap.DEFAULT
          const color = colorSalidaMap[exit.codigo] || colorSalidaMap.DEFAULT
          return (
            <Button
              key={index}
              startIcon={<Icon sx={{ fontSize: fontSize }} />}
              disabled={loading || modal}
              onClick={async () => {
                markAction(PresenceControlActions.Salida, exit.codigo)
              }}
              variant="contained"
              color={color}
              sx={{
                textWrap: 'nowrap',
                fontSize: fontSize,
              }}
            >
              {exit.descripcion}
            </Button>
          )
        })}
      </Box>
    </Box>
  )
}

export default PresenceActions
